import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogContentText, TextField, DialogActions, Button,
    InputAdornment, IconButton, Typography, Box, Alert, CircularProgress
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

function ApiKeyDialog({ open, currentKey, onSave, onClose, loading = false, error: authError = null, onClearError = null }) {
    const [keyInput, setKeyInput] = useState('');
    const [showKey, setShowKey] = useState(false);
    const [error, setError] = useState('');

    // Update local input state if the prop changes (e.g., loaded from storage)
    useEffect(() => {
        setKeyInput(currentKey || '');
    }, [currentKey, open]); // Re-sync when dialog opens or prop changes

    // Clear local error when dialog opens
    useEffect(() => {
        if (open) {
            setError('');
        }
    }, [open]);

    const handleSave = () => {
        if (!keyInput.trim()) {
            setError('API key cannot be empty');
            return;
        }
        setError('');
        onSave(keyInput);
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            handleSave();
        }
    };

    const toggleVisibility = () => {
        setShowKey(!showKey);
    }

    return (
        <Dialog 
            open={open} 
            onClose={onClose} 
            aria-labelledby="form-dialog-title"
            PaperProps={{
                elevation: 3,
                sx: { borderRadius: 2, maxWidth: 500 }
            }}
        >
            <DialogTitle id="form-dialog-title" sx={{ pb: 1 }}>
                <Box display="flex" alignItems="center">
                    <SecurityIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6">API Key Configuration</Typography>
                </Box>
            </DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ mb: 2 }}>
                    Please enter the API Key required to communicate with the server.
                    This key will be stored in your browser's local storage.
                </DialogContentText>
                
                {loading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <CircularProgress size={20} />
                        <Typography variant="body2" color="text.secondary">
                            Validating credentials...
                        </Typography>
                    </Box>
                ) : (error || authError) && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {authError || error}
                    </Alert>
                )}
                
                <TextField
                    autoFocus
                    margin="dense"
                    id="apiKey"
                    label="API Key"
                    type={showKey ? 'text' : 'password'}
                    fullWidth
                    variant="outlined"
                    value={keyInput}
                    onChange={(e) => {
                        setKeyInput(e.target.value);
                        // Clear auth error when user starts typing
                        if (authError && onClearError) {
                            onClearError();
                        }
                    }}
                    onKeyPress={handleKeyPress}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="toggle key visibility"
                                    onClick={toggleVisibility}
                                    edge="end"
                                >
                                    {showKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button onClick={onClose} variant="outlined" disabled={loading}>
                    Cancel
                </Button>
                <Button 
                    onClick={handleSave} 
                    variant="contained" 
                    disabled={!keyInput || loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <SecurityIcon />}
                >
                    {loading ? 'Validating...' : 'Save Key'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default ApiKeyDialog;